/**
 * <team-directory>
 *
 * League filtering, sorting, the live count and progressive reveal, all over a
 * grid that is already fully rendered. Every team is in the page, so narrowing
 * the set never costs a round trip — which is the whole reason the grid is
 * rendered server side rather than fetched.
 *
 * `shop_by_team` has no field to sort on in Liquid (`sort:` cannot read a
 * property off a metaobject drop), so the opening A–Z order is applied here.
 */
const DESKTOP = '(min-width: 750px)';
const COLUMNS_DESKTOP = 6;
const COLUMNS_MOBILE = 3;

if (!customElements.get('team-directory')) {
  customElements.define(
    'team-directory',
    class TeamDirectory extends HTMLElement {
      constructor() {
        super();
        this.onLeagueClick = this.onLeagueClick.bind(this);
        this.onExternalFilter = this.onExternalFilter.bind(this);
        this.onSortClick = this.onSortClick.bind(this);
        this.onLoadMore = this.onLoadMore.bind(this);
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.onHashChange = this.onHashChange.bind(this);

        this.league = '';
        this.sort = 'az';
      }

      connectedCallback() {
        this.grid = this.querySelector('[data-team-directory-grid]');
        this.countEl = this.querySelector('[data-team-directory-count]');
        this.emptyEl = this.querySelector('[data-team-directory-empty]');
        this.moreEl = this.querySelector('[data-team-directory-more]');
        this.moreButton = this.moreEl?.querySelector('.team-directory__more-button');
        this.sortDetails = this.querySelector('[data-team-directory-sort]');

        if (!this.grid) return;

        this.items = Array.from(this.grid.children);
        this.countTemplate = this.dataset.countTemplate || '';
        this.step = Number(this.dataset.initialCount) || this.items.length;
        this.revealed = this.step;

        this.leagueButtons = Array.from(this.querySelectorAll('[data-league-filter]'));
        this.sortButtons = Array.from(this.querySelectorAll('[data-sort]'));

        this.leagueButtons.forEach((button) => button.addEventListener('click', this.onLeagueClick));
        this.sortButtons.forEach((button) => button.addEventListener('click', this.onSortClick));
        if (this.moreButton) this.moreButton.addEventListener('click', this.onLoadMore);

        document.addEventListener('team-directory:filter', this.onExternalFilter);

        this.breakpoint = window.matchMedia(DESKTOP);
        this.breakpoint.addEventListener('change', this.onBreakpointChange);

        window.addEventListener('hashchange', this.onHashChange);

        // A league in the hash goes through the same entry point the pills use,
        // so it already paints. Only fall back to a plain apply() when there was
        // none, or the grid would be laid out twice on first load.
        if (!this.selectFromHash()) this.apply();
      }

      disconnectedCallback() {
        (this.leagueButtons || []).forEach((button) => button.removeEventListener('click', this.onLeagueClick));
        (this.sortButtons || []).forEach((button) => button.removeEventListener('click', this.onSortClick));
        if (this.moreButton) this.moreButton.removeEventListener('click', this.onLoadMore);
        document.removeEventListener('team-directory:filter', this.onExternalFilter);
        if (this.breakpoint) this.breakpoint.removeEventListener('change', this.onBreakpointChange);
        window.removeEventListener('hashchange', this.onHashChange);
      }

      onLeagueClick(event) {
        this.setLeague(event.currentTarget.dataset.leagueFilter);
      }

      /**
       * Fired by the hero's suggested-search chips. Selecting a league from
       * there has to look exactly like pressing the pill — including the pill
       * itself becoming the pressed one — or the grid and the controls disagree
       * about what is being shown.
       */
      onExternalFilter(event) {
        this.setLeague(event.detail?.league);
      }

      /**
       * The hash changing on an open page selects the league, the same as arriving
       * with it. Scrolled because that is what following a link to an anchor does,
       * and the pills are otherwise off screen.
       */
      onHashChange() {
        this.selectFromHash({ scroll: true });
      }

      /**
       * `…/pages/teams-directory#triple-a` opens on the Triple-A pill.
       *
       * Matched against the pills' OWN values rather than a list written here, so
       * a league added in the admin works without this file changing — the pill
       * carries `data-league-filter="triple-a"` already, downcased in the Liquid.
       *
       * A hash that is not a league is left alone rather than reset to All. That
       * matters: the hero links here with `#all-teams`, which is the section's own
       * anchor, and it has to keep meaning "jump to the directory" and nothing more.
       *
       * Returns whether it selected anything, so the caller knows if it still owes
       * the grid an apply().
       */
      selectFromHash({ scroll = false } = {}) {
        if (!this.leagueButtons?.length) return false;

        let wanted = window.location.hash.replace('#', '');
        try {
          wanted = decodeURIComponent(wanted);
        } catch {
          // A malformed escape throws rather than returning the raw string; the
          // undecoded value is still worth matching against.
        }
        wanted = wanted.trim().toLowerCase();
        if (!wanted) return false;

        // The All pill carries an empty value on purpose, and `wanted` is never
        // empty by here, so it can never be matched by a hash.
        const match = this.leagueButtons.find((button) => (button.dataset.leagueFilter || '') === wanted);
        if (!match) return false;

        this.setLeague(match.dataset.leagueFilter);

        if (scroll) {
          const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          this.scrollIntoView({ behavior: still ? 'auto' : 'smooth', block: 'start' });
        }

        return true;
      }

      /**
       * Single entry point for choosing a league, whichever control asked.
       * Pressed state is matched on the value rather than on element identity,
       * so it works for a caller that has no element — and it presses the All
       * pill, whose `data-league-filter` is deliberately empty.
       */
      setLeague(league) {
        this.league = league || '';

        this.leagueButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String((button.dataset.leagueFilter || '') === this.league));
        });

        this.revealed = this.step;
        this.apply();
      }

      onSortClick(event) {
        this.sort = event.currentTarget.dataset.sort || 'az';
        this.sortButtons.forEach((button) => {
          button.setAttribute('aria-pressed', String(button === event.currentTarget));
        });

        if (this.sortDetails) this.sortDetails.open = false;
        this.apply();
      }

      onLoadMore() {
        this.revealed += this.step;
        this.apply();
      }

      onBreakpointChange() {
        this.markRows();
      }

      /** Filter, order, reveal, then bring the readouts back in line. */
      apply() {
        const matching = this.items.filter((item) => !this.league || item.dataset.league === this.league);

        const ordered = [...matching].sort((a, b) => {
          const comparison = (a.dataset.name || '').localeCompare(b.dataset.name || '');
          return this.sort === 'za' ? -comparison : comparison;
        });

        // Non-matching items are hidden rather than detached, so a later filter
        // can bring them straight back without re-rendering anything.
        this.items.forEach((item) => {
          item.hidden = true;
        });

        ordered.forEach((item, index) => {
          item.hidden = index >= this.revealed;
        });

        this.grid.append(...ordered);

        this.updateCount(matching.length);
        this.markRows();

        if (this.emptyEl) this.emptyEl.hidden = matching.length > 0;
        if (this.moreEl) this.moreEl.hidden = matching.length <= this.revealed;
      }

      updateCount(total) {
        if (!this.countEl || !this.countTemplate) return;
        this.countEl.textContent = this.countTemplate.replace('__COUNT__', String(total));
      }

      /**
       * The mobile frame draws hairlines between columns and between rows.
       * nth-child cannot express that here because hidden items still count, so
       * the row positions are marked on the visible items instead.
       */
      markRows() {
        const columns = this.breakpoint.matches ? COLUMNS_DESKTOP : COLUMNS_MOBILE;

        this.items.forEach((item) => {
          item.removeAttribute('data-row-start');
          item.removeAttribute('data-first-row');
        });

        this.items
          .filter((item) => !item.hidden)
          .forEach((item, index) => {
            if (index % columns === 0) item.setAttribute('data-row-start', '');
            if (index < columns) item.setAttribute('data-first-row', '');
          });
      }
    },
  );
}
